import { SITE_CHAT_LINK_TYPES, SITE_CHAT_MAX_LINKS, type SiteChatLink, type SiteChatLinkType } from './types';

/** Brand names stay as they are in every language. */
const BRANDS: Partial<Record<SiteChatLinkType, string>> = {
    telegram: 'Telegram',
    whatsapp: 'WhatsApp',
    instagram: 'Instagram',
    facebook: 'Facebook',
    vk: 'VK',
    viber: 'Viber',
};

/** Where a profile name goes when the owner typed `@name` or `name` instead of a link. */
const PROFILE_HOSTS: Partial<Record<SiteChatLinkType, string>> = {
    telegram: 't.me',
    instagram: 'instagram.com',
    facebook: 'facebook.com',
    vk: 'vk.com',
};

/** Keeps only well-formed rows: a known type and something to link to; at most six. */
export function normalizeLinks(raw: unknown): SiteChatLink[] {
    if (!Array.isArray(raw))
        return [];
    return raw
        .filter((item): item is SiteChatLink => !!item && typeof item === 'object' && SITE_CHAT_LINK_TYPES.includes((item as SiteChatLink).type))
        .map(item => ({ type: item.type, label: String(item.label ?? '').slice(0, 60), value: String(item.value ?? '').slice(0, 300) }))
        .slice(0, SITE_CHAT_MAX_LINKS);
}

function digits(value: string) {
    return value.replace(/\D/g, '');
}

/**
 * The URL a row opens, or null when there is nothing usable in it (the row is then not drawn). Only http(s),
 * mailto, tel and Viber's own scheme come out of here — never `javascript:` or another scheme a value could carry.
 */
export function linkHref(link: SiteChatLink): string | null {
    const value = (link.value ?? '').trim();
    if (!value)
        return null;
    const web = /^https?:\/\//i.test(value) ? value : null;
    switch (link.type) {
        case 'email':
            return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value.replace(/^mailto:/i, '')) ? `mailto:${value.replace(/^mailto:/i, '')}` : null;
        case 'phone': {
            const number = digits(value);
            return number.length >= 5 ? `tel:${value.trim().startsWith('+') ? '+' : ''}${number}` : null;
        }
        case 'whatsapp': {
            if (web)
                return web;
            const number = digits(value);
            return number.length >= 5 ? `https://wa.me/${number}` : null;
        }
        case 'viber': {
            if (/^viber:\/\//i.test(value))
                return value;
            if (web)
                return web;
            const number = digits(value);
            return number.length >= 5 ? `viber://chat?number=%2B${number}` : null;
        }
        case 'url':
            if (web)
                return web;
            return /^[a-z]+:/i.test(value) ? null : `https://${value.replace(/^\/+/, '')}`;
        default: {
            if (web)
                return web;
            const host = PROFILE_HOSTS[link.type]!;
            // `t.me/school`, `instagram.com/school` without the scheme.
            if (value.toLowerCase().startsWith(`${host}/`) || value.toLowerCase().startsWith(`www.${host}/`))
                return `https://${value}`;
            const name = value.replace(/^@+/, '').replace(/\s+/g, '');
            return /^[\w.-]+$/.test(name) ? `https://${host}/${name}` : null;
        }
    }
}

/** The row's text: the owner's own, else the brand, else the address (email, phone) or the page's host. */
export function linkLabel(link: SiteChatLink): string {
    const label = link.label?.trim();
    if (label)
        return label;
    const brand = BRANDS[link.type];
    if (brand)
        return brand;
    const value = (link.value ?? '').trim().replace(/^mailto:/i, '');
    if (link.type === 'url') {
        try {
            return new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`).host.replace(/^www\./, '');
        } catch {
            return value;
        }
    }
    return value;
}

export function brandName(type: SiteChatLinkType): string | undefined {
    return BRANDS[type];
}
